import { useMutation } from '@tanstack/react-query'

// import { signIn, signOut } from 'aws-amplify/auth'
import { postRequest } from 'src/lib/api-client'
import { logger } from 'src/lib/logger'
import { sendEvent } from 'src/shared/api/events/send-event'

import { AUTH_ERROR_CODES, AuthError } from '../lib/auth-errors'

interface ConsumerPhoneInitiateResponse {
  success: boolean
  message: string | null
  phoneHint: string | null
  expiresInSeconds: number | null
  userExists: boolean | null // For user endpoints
  consumerExists: boolean | null // For consumer endpoints
  error: string | null
}

/**
 * Starts the consumer phone signin process.
 * @param phoneNumber - The phone number to sign in with. E.164 format.
 */
const consumerPhoneInitiate = async (phoneNumber: string): Promise<ConsumerPhoneInitiateResponse | undefined> => {
  const result = await postRequest<ConsumerPhoneInitiateResponse>('/auth/consumer/phone/initiate', { phoneNumber })
  return result
}

export const useConsumerPhoneSignInStart = () => {
  return useMutation({
    mutationFn: async (phoneNumber: string) => {
      const result = await consumerPhoneInitiate(phoneNumber)

      if (!result || result.success === false) {
        throw new AuthError(AUTH_ERROR_CODES.INVALID_RESPONSE, 'No response or success is false')
      }

      // Throw error if user already exists as a professional.
      if (result.userExists === true && result.consumerExists === false) {
        throw new AuthError(
          AUTH_ERROR_CODES.PROFESSIONAL_EXISTS,
          'This phone number is linked to a professional account.',
        )
      }

      sendEvent({
        eventType: 'auth_consumer_phone_signin_otp_sent',
        info: { phoneNumber },
      })

      return result
    },
    onError: (error: unknown) => {
      const errorCode = error instanceof AuthError ? error.code : 'unknown'
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      logger.error('Sign-in start error:', errorCode, errorMessage)

      sendEvent({
        eventType: 'auth_consumer_signin_start_error',
        info: { error: errorCode, message: errorMessage },
      })
    },
  })
}

interface ConsumerEmailInitiateResponse {
  consumerExists: boolean
  emailHint: string
  expiresInMinutes: number
  message: string
  session: string
  success: boolean
  userExists: boolean
}

const consumerEmailInitiate = async (email: string) => {
  const result = await postRequest<ConsumerEmailInitiateResponse>('/auth/consumer/email/initiate', { email })
  return result
}

export const useConsumerSignInStart = () => {
  return useMutation({
    mutationFn: async (email: string) => {
      const result = await consumerEmailInitiate(email)

      if (!result || result.success === false) {
        throw new AuthError(AUTH_ERROR_CODES.INVALID_RESPONSE, 'No response or success is false')
      }

      // Throw error if user already exists as a professional.
      if (result.userExists === true && result.consumerExists === false) {
        throw new AuthError(AUTH_ERROR_CODES.PROFESSIONAL_EXISTS, 'This email is linked to a professional account.')
      }

      sendEvent({
        eventType: 'auth_consumer_signin_otp_sent',
        info: { email },
      })

      return result
    },
    onError: (error: unknown) => {
      const errorCode = error instanceof AuthError ? error.code : 'unknown'
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      logger.error('Sign-in start error:', errorCode, errorMessage)

      sendEvent({
        eventType: 'auth_consumer_signin_start_error',
        info: { error: errorCode, message: errorMessage },
      })
    },
  })
}
